import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, selectCartState } from '../../../../redux/WithcookieSlice';

import { SearchBar } from '../ui/input/SearchBar';
import { LoginIcon, CartIcon, ArrowIcon, CloseIcon, UserIcon, LogoutIcon } from '../icons';
import { GNB_MOCK_DATA } from '../../constants/navigation';

import type { GNBData, SideBarPropsType } from '../../types/navigation';

gsap.registerPlugin(ScrollTrigger);

/**
 * SideBar 컴포넌트 - 모바일 전용 사이드 메뉴
 *
 * 구조:
 * - 오버레이 배경 + 슬라이딩 사이드바 메뉴
 * - 2단계 메뉴 시스템: 메인 메뉴 → 메가메뉴 (하위 카테고리)
 * - 각 하위 카테고리별 드롭다운 아코디언
 *
 * 상태 관리:
 * - isOpen: 사이드바 열림/닫힘 (부모로부터 전달)
 * - clickedItem: 선택된 메뉴 아이템 (메가메뉴용)
 * - isMegaMenuOpen: 메가메뉴 열림/닫힘 상태
 * - expandedDropdowns: 현재 열린 드롭다운들의 Set
 *
 * 애니메이션:
 * - GSAP Timeline으로 사이드바 슬라이드 인/아웃
 * - 메가메뉴 페이드 인/아웃 및 슬라이드 전환
 * - 개별 드롭다운 아코디언 애니메이션 (height 기반)
 *
 * Props:
 * - isOpen: boolean - 사이드바 표시 여부
 * - onClose: () => void - 사이드바 닫기 콜백
 */
interface ExtendedSideBarPropsType extends SideBarPropsType {
    menus?: any[];
    isLoggedIn?: boolean;
    isBusiness?: boolean;
}

export function SideBar({ isOpen, onClose, menus = [], isLoggedIn = false, isBusiness = false }: ExtendedSideBarPropsType) {
    
    // URL 처리 함수
    const getUrl = (menuItem: any) => {
        // 외부 링크 처리
        if (menuItem.externalLink) {
            return menuItem.externalLink;
        }
        // path를 우선적으로 사용 (flone과 동일)
        if (menuItem.path) {
            return menuItem.path;
        }
        if (menuItem.url) {
            return menuItem.url;
        }
        if (menuItem.link) {
            return menuItem.link;
        }
        if (menuItem.menu_srl) {
            return `/board/${menuItem.menu_srl}`;
        }
        return '#';
    };

    // 웹빌더 메뉴 데이터를 GNBData 형식으로 변환 (Gnb와 동일한 방식)
    const navigationData = menus.length > 0 ? (() => {
        // 메뉴 정렬 (order 기준)
        const sortedMenus = [...menus].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // 1단계: parentId가 null인 루트 메뉴 찾기 + 필터링 적용
        const rootMenus = sortedMenus.filter(menu => {
            // parentId가 있는 메뉴는 하위 메뉴이므로 제외
            if (menu.parentId !== null) {
                return false;
            }
            
            // 글로벌 메뉴는 제외
            if (menu.type === 'GLOBAL') {
                return false;
            }
            
            // visible이 false인 메뉴 제외
            if (menu.visible === false) {
                return false;
            }
            
            // 사업자 전용 메뉴 필터링
            if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                return false;
            }
            
            // 로그인 상태별 메뉴 필터링
            if (!menu.is_business_only) {
                // 로그인 관련 속성이 없는 경우 모두 표시
                if (!menu.is_logged && !menu.is_not_logged) {
                    return true;
                }
                // 로그인 필요 메뉴는 로그인 시만 표시
                if (menu.is_logged && !isLoggedIn) {
                    return false;
                }
                // 비로그인 메뉴는 로그아웃 시만 표시
                if (menu.is_not_logged && isLoggedIn) {
                    return false;
                }
            }
            
            return true;
        });
        
        // 2단계: 각 루트 메뉴에 대해 계층 구조 구축
        return rootMenus.map(rootMenu => {
            // 현재 메뉴의 자식 찾기 (로그인 상태 필터링 적용)
            const children = sortedMenus.filter(menu => {
                if (menu.parentId !== rootMenu.id) return false;
                if (menu.visible === false) return false;
                
                // 사업자 전용 메뉴 필터링
                if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                    return false;
                }
                
                // 로그인 상태별 메뉴 필터링
                if (!menu.is_business_only) {
                    if (!menu.is_logged && !menu.is_not_logged) {
                        return true;
                    }
                    if (menu.is_logged && !isLoggedIn) {
                        return false;
                    }
                    if (menu.is_not_logged && isLoggedIn) {
                        return false;
                    }
                }
                
                return true;
            });
            
            return {
                label: rootMenu.name || rootMenu.title || rootMenu.menu_name || '메뉴',
                to: getUrl(rootMenu),
                externalLink: rootMenu.externalLink,
                children: children.map(child => {
                    // 자식의 자식(손자) 찾기 (로그인 상태 필터링 적용)
                    const grandChildren = sortedMenus.filter(menu => {
                        if (menu.parentId !== child.id) return false;
                        if (menu.visible === false) return false;
                        
                        // 사업자 전용 메뉴 필터링
                        if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                            return false;
                        }
                        
                        // 로그인 상태별 메뉴 필터링
                        if (!menu.is_business_only) {
                            if (!menu.is_logged && !menu.is_not_logged) {
                                return true;
                            }
                            if (menu.is_logged && !isLoggedIn) {
                                return false;
                            }
                            if (menu.is_not_logged && isLoggedIn) {
                                return false;
                            }
                        }
                        
                        return true;
                    });
                    
                    return {
                        label: child.name || child.title || child.menu_name || '서브메뉴',
                        to: getUrl(child),
                        children: grandChildren.map(grandChild => ({
                            label: grandChild.name || grandChild.title || grandChild.menu_name || '서브메뉴',
                            to: getUrl(grandChild)
                        }))
                    };
                })
            };
        });
    })() : GNB_MOCK_DATA;
    const [clickedItem, setClickedItem] = useState<GNBData | null>(null);
    const [isMegaMenuOpen, setIsMegaMenuOpen] = useState<boolean>(false);
    const [megaMenuTimeline, setMegaMenuTimeline] = useState<any>(null);
    const [expandedDropdowns, setExpandedDropdowns] = useState<Set<string>>(new Set());

    function handleOpenMegaMenu(item: GNBData) {
        setClickedItem(item);
        setIsMegaMenuOpen(true);
    }

    function handleCloseMegaMenu() {
        setIsMegaMenuOpen(false);
        // 메가메뉴가 닫힐 때 모든 드롭다운도 초기화
        setExpandedDropdowns(new Set());
    }

    function toggleDropdown(childTo: string) {
        const newExpanded = new Set(expandedDropdowns);
        const wasExpanded = newExpanded.has(childTo);

        if (wasExpanded) {
            newExpanded.delete(childTo);
        } else {
            newExpanded.add(childTo);
        }
        setExpandedDropdowns(newExpanded);
    }

    // 드롭다운 개별 애니메이션
    useGSAP(() => {
        if (!clickedItem || !clickedItem.children) return;

        // 현재 상태에 따라 각 드롭다운의 애니메이션 결정
        clickedItem.children.forEach((child) => {
            const dropdown = document.querySelector(`[data-dropdown="${child.to}"]`);
            const button = document.querySelector(`[data-dropdown-btn="${child.to}"]`);

            if (dropdown && button) {
                const shouldBeExpanded = expandedDropdowns.has(child.to);
                const dropdownElement = dropdown as HTMLElement;

                // CSS 클래스로 애니메이션 처리
                if (shouldBeExpanded) {
                    dropdown.classList.add('expanded');
                    button.classList.add('expanded');
                    // 높이 계산
                    const height = dropdownElement.scrollHeight;
                    dropdownElement.style.height = `${height}px`;
                } else {
                    dropdown.classList.remove('expanded');
                    button.classList.remove('expanded');
                    dropdownElement.style.height = '0px';
                }

                // GSAP 애니메이션 시도
                if (typeof gsap !== 'undefined') {
                    const buttonSvg = button.querySelector('svg');
                    const currentHeight = dropdownElement.style.height;
                    const isCurrentlyExpanded = currentHeight !== '0px' && currentHeight !== '';

                    // 상태가 바뀐 경우에만 애니메이션 실행
                    if (shouldBeExpanded !== isCurrentlyExpanded) {
                        // 기존 애니메이션 중단
                        gsap.killTweensOf(dropdown);
                        gsap.killTweensOf(buttonSvg);

                        if (shouldBeExpanded) {
                            // 드롭다운 열기 애니메이션
                            gsap.set(dropdown, {
                                height: 'auto',
                                overflow: 'hidden',
                            });

                            const height = dropdown.scrollHeight;

                            gsap.fromTo(
                                dropdown,
                                {
                                    height: 0,
                                    opacity: 0,
                                },
                                {
                                    height: height,
                                    opacity: 1,
                                    duration: 0.4,
                                    ease: 'power2.out',
                                }
                            );

                            // 버튼 화살표 회전
                            if (buttonSvg) {
                                gsap.to(buttonSvg, {
                                    rotation: 0,
                                    duration: 0.3,
                                    ease: 'power2.out',
                                });
                            }
                        } else {
                            // 드롭다운 닫기 애니메이션
                            gsap.to(dropdown, {
                                height: 0,
                                opacity: 0,
                                duration: 0.4,
                                ease: 'power2.out',
                            });

                            // 버튼 화살표 원래 위치로
                            if (buttonSvg) {
                                gsap.to(buttonSvg, {
                                    rotation: 180,
                                    duration: 0.3,
                                    ease: 'power2.out',
                                });
                            }
                        }
                    }
                } else {
                    // GSAP이 없을 때 직접 스타일 설정
                    if (shouldBeExpanded) {
                        dropdownElement.style.opacity = '1';
                    } else {
                        dropdownElement.style.opacity = '0';
                    }
                }
            }
        });
    }, [expandedDropdowns, clickedItem]);

    // 컴포넌트 언마운트 시 타임라인 정리
    useEffect(() => {
        return () => {
            if (megaMenuTimeline) {
                megaMenuTimeline.kill();
            }
        };
    }, [megaMenuTimeline]);

    // 메가메뉴가 닫힐 때 모든 드롭다운 초기화
    useGSAP(() => {
        if (!isMegaMenuOpen && clickedItem && clickedItem.children) {
            // 메가메뉴가 닫혔을 때만 모든 드롭다운 초기화
            clickedItem.children.forEach((child) => {
                const dropdown = document.querySelector(`[data-dropdown="${child.to}"]`);
                const button = document.querySelector(`[data-dropdown-btn="${child.to}"]`);

                if (dropdown && button) {
                    // CSS 클래스 제거
                    dropdown.classList.remove('expanded');
                    button.classList.remove('expanded');
                    (dropdown as HTMLElement).style.height = '0px';
                    (dropdown as HTMLElement).style.opacity = '0';

                    // GSAP 애니메이션 중단 시도
                    if (typeof gsap !== 'undefined') {
                        const buttonSvg = button.querySelector('svg');
                        gsap.killTweensOf(dropdown);
                        if (buttonSvg) {
                            gsap.killTweensOf(buttonSvg);
                            gsap.set(dropdown, { height: 0, opacity: 0 });
                            gsap.set(buttonSvg, { rotation: 0 });
                        }
                    }
                }
            });
        }
    }, [isMegaMenuOpen]);

    // 사이드 바 fade-in-out 애니메이션
    useEffect(() => {
        const container = document.querySelector('#sideBar');
        const overlay = document.querySelector('.sideBarOverlay');
        const content = document.querySelector('.sideBarContent');

        // CSS 클래스를 사용한 애니메이션 (GSAP 실패 시 대비)
        if (container) {
            if (isOpen) {
                container.classList.add('sideBar-open');
            } else {
                container.classList.remove('sideBar-open');
                // 애니메이션 후 상태 초기화
                setTimeout(() => {
                    if (!isOpen) {
                        setIsMegaMenuOpen(false);
                        setClickedItem(null);
                        setExpandedDropdowns(new Set());
                        megaMenuTimeline?.kill();
                    }
                }, 500);
            }
        }

        // GSAP 애니메이션 시도
        if (typeof gsap !== 'undefined' && container && overlay && content) {
            if (isOpen) {
                gsap.set(container, { visibility: 'visible', width: '100%' });
                gsap.to(overlay, {
                    opacity: 1,
                    duration: 0.5,
                    ease: 'power2.out',
                });
                gsap.to(content, {
                    x: '0%',
                    opacity: 1,
                    duration: 0.5,
                    ease: 'power2.out',
                });
            } else {
                gsap.to(overlay, {
                    opacity: 0,
                    duration: 0.5,
                    ease: 'power2.out',
                });
                gsap.to(content, {
                    x: '-5%',
                    opacity: 0,
                    duration: 0.5,
                    ease: 'power2.out',
                    onComplete: () => {
                        gsap.set(container, { visibility: 'hidden', width: '0%' });
                        setIsMegaMenuOpen(false);
                        setClickedItem(null);
                        setExpandedDropdowns(new Set());
                        megaMenuTimeline?.kill();
                    },
                });
            }
        } else if (container && overlay && content) {
            // GSAP이 없을 때는 직접 스타일 설정
            if (isOpen) {
                (container as HTMLElement).style.visibility = 'visible';
                (container as HTMLElement).style.width = '100%';
                (overlay as HTMLElement).style.opacity = '1';
                (content as HTMLElement).style.transform = 'translateX(0%)';
                (content as HTMLElement).style.opacity = '1';
            } else {
                setTimeout(() => {
                    (container as HTMLElement).style.visibility = 'hidden';
                    (container as HTMLElement).style.width = '0%';
                }, 500);
                (overlay as HTMLElement).style.opacity = '0';
                (content as HTMLElement).style.transform = 'translateX(-5%)';
                (content as HTMLElement).style.opacity = '0';
                setIsMegaMenuOpen(false);
                setClickedItem(null);
                setExpandedDropdowns(new Set());
            }
        }
    }, [isOpen]);

    // 메가 메뉴 애니메이션
    useGSAP(() => {
        const menu = document.querySelector('.sideBarMenu');
        const megaMenu = document.querySelector('.sideBarMegaMenu');

        // CSS 클래스를 사용한 애니메이션 (GSAP 실패 시 대비)
        if (menu && megaMenu) {
            if (isMegaMenuOpen) {
                menu.classList.add('menu-hidden');
                megaMenu.classList.add('mega-visible');
                (megaMenu as HTMLElement).style.visibility = 'visible';
            } else {
                menu.classList.remove('menu-hidden');
                megaMenu.classList.remove('mega-visible');
                setTimeout(() => {
                    if (!isMegaMenuOpen) {
                        (megaMenu as HTMLElement).style.visibility = 'hidden';
                        setClickedItem(null);
                    }
                }, 500);
            }
        }

        // GSAP 애니메이션 시도
        if (typeof gsap !== 'undefined' && menu && megaMenu) {
            // 기존 타임라인이 있으면 중단
            if (megaMenuTimeline) {
                megaMenuTimeline.kill();
            }

            if (isMegaMenuOpen) {
                const tl = gsap.timeline();
                tl.set(megaMenu, { opacity: 0, x: '5%', visibility: 'visible' })
                    .to(menu, {
                        opacity: 0,
                        x: '-5%',
                        duration: 0.5,
                        ease: 'power2.out',
                    })
                    .to(
                        megaMenu,
                        {
                            opacity: 1,
                            x: 0,
                            duration: 0.5,
                            ease: 'power2.out',
                        },
                        '<'
                    );

                setMegaMenuTimeline(tl);
            } else {
                const tl = gsap.timeline();
                tl.to(megaMenu, {
                    opacity: 0,
                    x: '5%',
                    duration: 0.5,
                    ease: 'power2.out',
                })
                    .to(
                        menu,
                        {
                            opacity: 1,
                            x: '0%',
                            duration: 0.5,
                            ease: 'power2.out',
                        },
                        '<'
                    )
                    .set(megaMenu, {
                        visibility: 'hidden',
                    })
                    .call(() => {
                        setClickedItem(null);
                    });

                setMegaMenuTimeline(tl);
            }
        } else if (menu && megaMenu) {
            // GSAP이 없을 때 직접 스타일 설정
            if (isMegaMenuOpen) {
                (menu as HTMLElement).style.opacity = '0';
                (menu as HTMLElement).style.transform = 'translateX(-5%)';
                (megaMenu as HTMLElement).style.visibility = 'visible';
                (megaMenu as HTMLElement).style.opacity = '1';
                (megaMenu as HTMLElement).style.transform = 'translateX(0%)';
            } else {
                (menu as HTMLElement).style.opacity = '1';
                (menu as HTMLElement).style.transform = 'translateX(0%)';
                (megaMenu as HTMLElement).style.opacity = '0';
                (megaMenu as HTMLElement).style.transform = 'translateX(5%)';
                setTimeout(() => {
                    (megaMenu as HTMLElement).style.visibility = 'hidden';
                    setClickedItem(null);
                }, 500);
            }
        }
    }, [clickedItem, isMegaMenuOpen]);

    // 메가메뉴가 닫힐 때만 모든 드롭다운 초기화
    useGSAP(() => {
        if (!isMegaMenuOpen && clickedItem && clickedItem.children) {
            // 메가메뉴가 닫혔을 때만 모든 드롭다운 초기화
            clickedItem.children.forEach((child) => {
                const dropdown = document.querySelector(`[data-dropdown="${child.to}"]`);
                const button = document.querySelector(`[data-dropdown-btn="${child.to}"]`);

                if (dropdown && button) {
                    // CSS 클래스 제거
                    dropdown.classList.remove('expanded');
                    button.classList.remove('expanded');
                    (dropdown as HTMLElement).style.height = '0px';
                    (dropdown as HTMLElement).style.opacity = '0';

                    // GSAP 애니메이션 중단 시도
                    if (typeof gsap !== 'undefined') {
                        const buttonSvg = button.querySelector('svg');
                        gsap.killTweensOf(dropdown);
                        if (buttonSvg) {
                            gsap.killTweensOf(buttonSvg);
                            gsap.set(dropdown, { height: 0, opacity: 0 });
                            gsap.set(buttonSvg, { rotation: 180 });
                        }
                    }
                }
            });
        }
    }, [isMegaMenuOpen]);

    return (
        <section
            id="sideBar"
            className={`sideBar fixed top-0 left-0 h-full overflow-hidden z-[100002] w-0 invisible`}
        >
            <SideBarOverlay onClose={onClose!} />
            <SideBarContent
                onClose={onClose!}
                isOpen={isOpen}
                clickedItem={clickedItem}
                handleOpenMegaMenu={handleOpenMegaMenu}
                handleCloseMegaMenu={handleCloseMegaMenu}
                toggleDropdown={toggleDropdown}
                navigationData={navigationData}
                isLoggedIn={isLoggedIn}
            />
        </section>
    );
}

// 사이드바 오버레이 컴포넌트
function SideBarOverlay({ onClose }: { onClose: () => void }) {
    return (
        <div
            className="sideBarOverlay fixed bg-black/60 w-full h-full"
            style={{ opacity: 0 }}
            onClick={onClose}
            onTouchEnd={onClose}
        />
    );
}

// 사이드바 콘텐츠 컴포넌트
function SideBarContent({
    onClose,
    isOpen,
    clickedItem,
    handleOpenMegaMenu,
    handleCloseMegaMenu,
    toggleDropdown,
    navigationData,
    isLoggedIn,
}: {
    onClose: () => void;
    isOpen: boolean;
    clickedItem: GNBData | null;
    handleOpenMegaMenu: (item: GNBData) => void;
    handleCloseMegaMenu: () => void;
    toggleDropdown: (childTo: string) => void;
    navigationData: GNBData[];
    isLoggedIn: boolean;
}) {
    return (
        <div className="sideBarContent fixed left-0 bg-white w-full max-w-[400px] h-full flex flex-col opacity-0 -translate-x-[5%]">
            <SideBarHeader onClose={onClose} />
            <SideBarSearchSection
                isOpen={isOpen}
                onClose={onClose}
            />
            <SideBarNavigation
                clickedItem={clickedItem}
                handleOpenMegaMenu={handleOpenMegaMenu}
                handleCloseMegaMenu={handleCloseMegaMenu}
                toggleDropdown={toggleDropdown}
                onClose={onClose}
                navigationData={navigationData}
            />
            <SideBarFooter onClose={onClose} isLoggedIn={isLoggedIn} />
        </div>
    );
}

// 사이드바 헤더 컴포넌트
function SideBarHeader({ onClose }: { onClose: () => void }) {
    return (
        <div className="flex items-center justify-end p-2">
            <button
                className="closeBtn p-3.5"
                aria-label="메뉴 닫기"
                type="button"
                onClick={onClose}
            >
                <CloseIcon />
            </button>
        </div>
    );
}

// 사이드바 검색 섹션 컴포넌트
function SideBarSearchSection({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    return (
        <div className="p-5">
            <SearchBar
                directionReverse
                hideCloseBtn
                isResetValue={!isOpen}
                submitCallback={onClose}
            />
        </div>
    );
}

// 사이드바 네비게이션 컴포넌트
function SideBarNavigation({ clickedItem, handleOpenMegaMenu, handleCloseMegaMenu, toggleDropdown, onClose, navigationData }: { clickedItem: GNBData | null; handleOpenMegaMenu: (item: GNBData) => void; handleCloseMegaMenu: () => void; toggleDropdown: (childTo: string) => void; onClose: () => void; navigationData: GNBData[] }) {
    return (
        <nav className="sideBarGnb grow relative">
            <SideBarMainMenu
                handleOpenMegaMenu={handleOpenMegaMenu}
                onClose={onClose}
                navigationData={navigationData}
            />
            <SideBarMegaMenu
                clickedItem={clickedItem}
                handleCloseMegaMenu={handleCloseMegaMenu}
                toggleDropdown={toggleDropdown}
                onClose={onClose}
            />
        </nav>
    );
}

// 사이드바 메인 메뉴 컴포넌트
function SideBarMainMenu({ handleOpenMegaMenu, onClose, navigationData }: { handleOpenMegaMenu: (item: GNBData) => void; onClose: () => void; navigationData: GNBData[] }) {
    return (
        <ul className="sideBarMenu space-y-8 absolute top-0 left-0 w-full h-full overflow-y-auto">
            {navigationData.map((item, index) => (
                <li
                    key={`menu-${index}-${item.label}`}
                    className="flex items-center justify-between px-5"
                >
                    <h3>
                        <a
                            href={item.to}
                            className="font-serif text-3xl leading-[1.2] hover-primary-80"
                            onClick={onClose}
                        >
                            {item.label}
                        </a>
                    </h3>
                    {item.children && item.children.length > 0 && (
                        <button
                            type="button"
                            className="flex items-center justify-center p-3 pr-1.5"
                            aria-label="메가 메뉴 열기"
                            onClick={() => handleOpenMegaMenu(item)}
                        >
                            <ArrowIcon rotate="90" />
                        </button>
                    )}
                </li>
            ))}
        </ul>
    );
}

// 사이드바 메가 메뉴 컴포넌트
function SideBarMegaMenu({ clickedItem, handleCloseMegaMenu, toggleDropdown, onClose }: { clickedItem: GNBData | null; handleCloseMegaMenu: () => void; toggleDropdown: (childTo: string) => void; onClose: () => void }) {
    return (
        <div className="sideBarMegaMenu absolute top-0 left-0 w-full h-full overflow-y-auto invisible">
            {clickedItem && clickedItem.children && (
                <>
                    <SideBarMegaMenuHeader
                        clickedItem={clickedItem}
                        handleCloseMegaMenu={handleCloseMegaMenu}
                    />
                    <SideBarMegaMenuList
                        clickedItem={clickedItem}
                        toggleDropdown={toggleDropdown}
                        onClose={onClose}
                    />
                </>
            )}
        </div>
    );
}

// 사이드바 메가 메뉴 헤더 컴포넌트
function SideBarMegaMenuHeader({ clickedItem, handleCloseMegaMenu }: { clickedItem: GNBData; handleCloseMegaMenu: () => void }) {
    return (
        <div className="flex items-center justify-start px-5 mb-9">
            <button
                type="button"
                className="flex items-center justify-center p-3 pl-1.5"
                aria-label="메가 메뉴 닫기"
                onClick={handleCloseMegaMenu}
            >
                <ArrowIcon rotate="270" />
            </button>
            <h3 className="font-serif text-3xl leading-[1.2] ml-4">{clickedItem.label}</h3>
        </div>
    );
}

// 사이드바 메가 메뉴 리스트 컴포넌트
function SideBarMegaMenuList({ clickedItem, toggleDropdown, onClose }: { clickedItem: GNBData; toggleDropdown: (childTo: string) => void; onClose: () => void }) {
    return (
        <ul className="sideBarMegaMenu">
            {clickedItem.children?.map((child, index) => (
                <li
                    key={`submenu-${index}-${child.label}`}
                    className="px-5 mb-4 last:mb-0"
                >
                    <div className="flex items-center justify-between">
                        <h4>
                            <a
                                href={child.to}
                                className="text-sm leading-[1.4] hover-primary-80"
                                onClick={onClose}
                            >
                                {child.label}
                            </a>
                        </h4>
                        {child.children && child.children.length > 0 && (
                            <button
                                type="button"
                                className="flex items-center justify-center p-3"
                                aria-label="하위 메뉴 열기"
                                data-dropdown-btn={child.to}
                                onClick={() => toggleDropdown(child.to)}
                            >
                                <ArrowIcon
                                    rotate="180"
                                    tailwind="w-[16px] h-[16px] text-black/40 hover-black transition-transform"
                                />
                            </button>
                        )}
                    </div>
                    {child.children && child.children.length > 0 && (
                        <ul
                            className="dropdown w-full mt-2 overflow-hidden"
                            data-dropdown={child.to}
                            style={{ height: 0, opacity: 0 }}
                        >
                            {child.children.map((subChild, subIndex) => (
                                <li
                                    key={`subchild-${subIndex}-${subChild.label}`}
                                    className="flex mb-3 last:mb-0"
                                >
                                    <a
                                        href={subChild.to}
                                        className="text-sm text-black/60 hover-black block leading-[1.4]"
                                        onClick={onClose}
                                    >
                                        {subChild.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </li>
            ))}
        </ul>
    );
}

// 사이드바 푸터 컴포넌트
function SideBarFooter({ onClose, isLoggedIn }: { onClose: () => void; isLoggedIn: boolean }) {
    const dispatch = useDispatch() as any;
    
    // Redux에서 장바구니 상태 가져오기
    const { cartItems } = useSelector(selectCartState);
    
    // 로그아웃 처리 함수
    const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onClose();
        dispatch(logoutUser()).then(() => {
            // 로그아웃 성공 후 홈 페이지로 이동
            window.location.href = '/';
        });
    };
    
    return (
        <nav className="sideBarMenu">
            <ul className="flex items-center justify-between py-2 mx-5 border-t border-black/10">
                <li className="flex items-center justify-center h-14 w-full">
                    {isLoggedIn ? (
                        <button
                            className="p-4"
                            aria-label="로그아웃"
                            type="button"
                        >
                            <a href="/logout" onClick={handleLogout}>
                                <LogoutIcon />
                            </a>
                        </button>
                    ) : (
                        <button
                            className="p-4"
                            aria-label="로그인"
                            type="button"
                            onClick={onClose}
                        >
                            <a href="/login">
                                <LoginIcon />
                            </a>
                        </button>
                    )}
                </li>
                <li className="relative flex items-center justify-center h-14 w-full before:content-[''] before:absolute before:top-1/2 before:left-0 before:h-5 before:w-px before:bg-black/10 before:transform before:-translate-y-1/2 after:content-[''] after:absolute after:top-1/2 after:right-0 after:h-5 after:w-px after:bg-black/10 after:transform after:-translate-y-1/2">
                    <button
                        className="p-4"
                        aria-label="마이페이지"
                        type="button"
                        onClick={onClose}
                    >
                        <a href="/mydata">
                            <UserIcon />
                        </a>
                    </button>
                </li>
                <li className="flex items-center justify-center h-14 w-full">
                    <button
                        className="p-4 pr-0"
                        aria-label="장바구니"
                        type="button"
                        onClick={onClose}
                    >
                        <a href="/cart">
                            <CartIcon />
                        </a>
                    </button>
                    <div className="bg-primary rounded-full w-6 h-6 flex items-center justify-center ml-1">
                        <span className="text-sm text-white font-bold">
                            {cartItems && cartItems.length ? cartItems.length : 0}
                        </span>
                    </div>
                </li>
            </ul>
        </nav>
    );
}
